import type { User } from "oidc-client-ts";
import type { DataPersonCv, Nullable } from "@/app/[lng]/cv/_lib/types";
import { REALMS } from "@/constants/process";
import { BaseRepositoryV2 } from "@/repositories/base-repository-v2";
import type { ApiResponse } from "@/repositories/common-types";
import type {
  EducationResponse,
  MeResponseType,
  MeSubmitType,
  PeopleRepositoryMethods,
  Person,
  PersonBody,
  StudyAreaResponse,
} from "@/repositories/people/types";
import type { RealmSliceState } from "@/store/realm-slice";

interface CvSaveEnvelope {
  data: DataPersonCv;
}

export default class PeopleRepository extends BaseRepositoryV2 implements PeopleRepositoryMethods {
  private static instance: PeopleRepository;

  // It's important to declare every method here and assign them as arrow functions
  // in the constructor to avoid errors in the client components that use them.
  path: string;
  getPersonCv;
  // getAllMotivators;
  firstTimeSignup;
  getPersonByEmail;
  getEducationByCountryId;
  me;
  submitMe;
  signup;
  submitCv;
  getAllStudyAreas;
  updatePersonalInfo;

  constructor() {
    super();
    this.path = "/people";

    // The difference between this method and `signup` is that this one is used
    // in the normal registration process. The user triggers the "normal" registration
    // flow from keycloak so a proper register in the evaluar platform is needed.
    this.firstTimeSignup = async (accessToken: User["access_token"]) => {
      const response = await this.post(
        `${this.path}/public/signup`,
        {
          token: accessToken,
        },
        {},
        false
      );
      return this.toAxiosResponse<Partial<Person>>(response);
    };
    //Example created for profile and CV
    this.getPersonCv = async () => {
      const response = await this.get(`${this.path}/persons/me/cv`, {}, true);
      return this.toAxiosResponse<DataPersonCv>(response);
    };

    this.getPersonByEmail = async (email: string) => {
      const response = await this.get(`${this.path}/public/persons/email/${email}`, {}, false);
      return this.toAxiosResponse<Partial<Person>>(response);
    };

    this.me = async (realm: RealmSliceState["realm"]) => {
      const response = await this.get(
        `${this.path}/persons/me`,
        {
          "x-provider-realm": realm || REALMS.EVCORE,
        },
        true
      );
      return this.toAxiosResponse<MeResponseType>(response);
    };

    this.signup = async (person: Partial<Person>, personId: number) => {
      const response = await this.put(`${this.path}/public/signup/${personId}`, person, {}, false);
      return this.toAxiosResponse<Partial<Person>>(response);
    };
    this.submitMe = async (formData: MeSubmitType) => {
      const coreRepo = new BaseRepositoryV2(
        `${typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_CORE_URL : ""}/people-web`
      );
      const response = await coreRepo.put("/persons/secure/me", formData, {}, true);
      return coreRepo.toAxiosResponse<void>(response);
    };
    this.submitCv = async function saveCv(
      formData: Nullable<DataPersonCv> | Partial<Person>
    ): Promise<ApiResponse<DataPersonCv>> {
      const coreRepo = new BaseRepositoryV2(
        `${typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_CORE_URL : ""}/people-web`
      );
      const response = await coreRepo.post("/persons/secure/me/cv", formData, {}, true);
      const savedResponse = await coreRepo.toAxiosResponse<CvSaveEnvelope | DataPersonCv>(response);
      if (savedResponse.data && "data" in savedResponse.data) {
        return { ...savedResponse, data: savedResponse.data.data };
      }
      return { ...savedResponse, data: savedResponse.data };
    };
    this.getEducationByCountryId = async (countryId: string) => {
      const coreRepo = new BaseRepositoryV2(
        `${typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_CORE_URL : ""}/api/people`
      );
      const response = await coreRepo.get(`/public/educations/${countryId}`, {}, false);
      return coreRepo.toAxiosResponse<EducationResponse>(response);
    };
    this.getAllStudyAreas = async () => {
      const studyAreasRepo = new BaseRepositoryV2("https://candidato.evaluardev.com/api/people");
      const response = await studyAreasRepo.get("/public/studyAreas", {}, false);
      return studyAreasRepo.toAxiosResponse<StudyAreaResponse>(response);
    };
    this.updatePersonalInfo = async (person: Partial<PersonBody>) => {
      const response = await this.put(`${this.path}/persons/secure/me`, person, {}, true);
      return this.toAxiosResponse<void>(response);
    };
  }

  /**
   * Gets the singleton instance of PeopleRepository
   * @returns The single instance of PeopleRepository
   */
  public static getInstance(): PeopleRepository {
    if (!PeopleRepository.instance) {
      PeopleRepository.instance = new PeopleRepository();
    }
    return PeopleRepository.instance;
  }
}
